import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import { useTranslation } from "react-i18next";

const COUNTRY_CONFIG = {
  Uganda: { code: "+256", min: 9, max: 9 },
  Kenya: { code: "+254", min: 9, max: 9 },
  USA: { code: "+1", min: 10, max: 10 },
  UK: { code: "+44", min: 10, max: 10 },
  Canada: { code: "+1", min: 10, max: 10 },
  India: { code: "+91", min: 10, max: 10 }
};

const LANGUAGE_OPTIONS = [
  { label: "Select Language", value: "" },
  { label: "English", value: "English" },
  { label: "Spanish", value: "Spanish" },
  { label: "French", value: "French" },
  { label: "Swahili", value: "Swahili" }
];

const RELATIONSHIP_OPTIONS = [
  { label: "Select Relationship", value: "" },
  { label: "Child", value: "Child" },
  { label: "Grandchild", value: "Grandchild" },
  { label: "Cousin", value: "Cousin" },
  { label: "Brother", value: "Brother" },
  { label: "Sister", value: "Sister" },
  { label: "Wife", value: "Wife" },
  { label: "Husband", value: "Husband" },
  { label: "Aunt", value: "Aunt" },
  { label: "Niece", value: "Niece" },
  { label: "Other", value: "Other" }
];

const CreateElderProfile = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("profile");
  const kinkeeperID = localStorage.getItem("kinkeeperID");
  const elderID = localStorage.getItem("elderID");
  const isEditMode = !!elderID;
  const [loading, setLoading] = useState(true);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [spokenLanguage, setSpokenLanguage] = useState("");
  const [gender, setGender] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [stateProvince, setStateProvince] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const [relationship, setRelationship] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchExisting = async () => {
      if (!isEditMode) {
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(`${config.API_BASE_URL}/patient/profile/${elderID}`, {
          params: { kinkeeperID }
        });

        const p = res.data.patient;
        setFirstName(p.firstName || "");
        setLastName(p.lastName || "");
        setSpokenLanguage(p.spokenLanguage || "");
        setGender(p.gender || "");
        setCountry(p.country || "");
        setCity(p.city || "");
        setStateProvince(p.stateProvince || "");
        setEmail(p.email || "");
        setRelationship(p.relationship || "");

        const code = COUNTRY_CONFIG[p.country]?.code || "";
        setCountryCode(code);
        setWhatsapp(p.whatsapp?.startsWith(code) ? p.whatsapp.replace(code, "") : p.whatsapp || "");
      } catch (err) {
        alert(t("create.loadFailed"));
        navigate("/elders/profile");
      } finally {
        setLoading(false);
      }
    };

    fetchExisting();
  }, [elderID, kinkeeperID, isEditMode, navigate, t]);

  const handleCountryChange = (e) => {
    const selected = e.target.value;
    setCountry(selected);
    setCountryCode(COUNTRY_CONFIG[selected]?.code || "");
    setWhatsapp("");
  };

  const handleWhatsappChange = (e) => {
    setWhatsapp(e.target.value.replace(/\D/g, ""));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!kinkeeperID) {
      alert(t("create.sessionExpired"));
      return false;
    }

    if (!firstName) newErrors.firstName = "First name is required";
    if (!lastName) newErrors.lastName = "Last name is required";
    if (!spokenLanguage) newErrors.spokenLanguage = "Select a language";
    if (!gender) newErrors.gender = "Select gender";
    if (!country) newErrors.country = "Select country";
    if (!city) newErrors.city = "City is required";
    if (!whatsapp) newErrors.whatsapp = "Phone number is required";
    if (!email) newErrors.email = "Email is required";
    if (!relationship) newErrors.relationship = "Select relationship";

    const cfg = COUNTRY_CONFIG[country];
    if (whatsapp && cfg && (whatsapp.length < cfg.min || whatsapp.length > cfg.max)) {
      newErrors.whatsapp = t("create.phoneLength", { min: cfg.min, max: cfg.max });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      const payload = {
        firstName,
        lastName,
        spokenLanguage,
        gender,
        country,
        city,
        stateProvince,
        whatsapp: `${countryCode}${whatsapp}`,
        email,
        relationship,
        kinkeeperID
      };

      if (isEditMode) {
        await axios.put(`${config.API_BASE_URL}/patient/update/${elderID}`, payload);
        alert(t("create.updateSuccess"));
      } else {
        const res = await axios.post(`${config.API_BASE_URL}/patient/create`, payload);
        localStorage.removeItem("patientID");
        localStorage.setItem("elderID", res.data.patientID);
        alert(t("create.createSuccess"));
      }

      navigate("/elders/profile");
    } catch (err) {
      alert(err.response?.data?.error || t("create.saveFailed"));
    }
  };

  if (loading) {
    return <div className="loading-state">{t("create.loading")}</div>;
  }

  return (
    <div className="page-shell">
      <section className="page-panel">
        <div className="page-content auth-layout profile-form-layout">
          <div className="support-card compact-support-card">
            <div>
              <div className="intro-badge">
                {isEditMode ? t("create.badgeEdit") : t("create.badgeCreate")}
              </div>
              <h1 className="page-title">
                {isEditMode ? t("create.titleEdit") : t("create.titleCreate")}
              </h1>
              <p className="page-subtitle">
                {t("create.subtitle")}
              </p>
            </div>

            <div className="support-list">
              <div className="support-item">
                <span className="support-dot" />
                <div>
                  <div className="mini-label">{t("create.support1Title")}</div>
                  <p>{t("create.support1Text")}</p>
                </div>
              </div>
              <div className="support-item">
                <span className="support-dot" />
                <div>
                  <div className="mini-label">{t("create.support2Title")}</div>
                  <p>{t("create.support2Text")}</p>
                </div>
              </div>
              <div className="support-item">
                <span className="support-dot" />
                <div>
                  <div className="mini-label">{t("create.support3Title")}</div>
                  <p>{t("create.support3Text")}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="form-card compact-form-card">
            <div className="form-header">
              <h2>{isEditMode ? t("create.formTitleEdit") : t("create.formTitleCreate")}</h2>
              <p>{t("create.formSubtitle")}</p>
            </div>

            <div className="form-grid">
              <Input
                label={t("fields.firstName")}
                value={firstName}
                setValue={setFirstName}
                error={errors.firstName}
              />
              <Input
                label={t("fields.lastName")}
                value={lastName}
                setValue={setLastName}
                error={errors.lastName}
              />

              <Input
                label={t("fields.email")}
                value={email}
                setValue={setEmail}
                error={errors.email}
              />
              <Select
                label={t("fields.relationship")}
                value={relationship}
                onChange={(e) => setRelationship(e.target.value)}
                options={RELATIONSHIP_OPTIONS}
                error={errors.relationship}
              />

              <Select
                label={t("fields.language")}
                value={spokenLanguage}
                onChange={(e) => setSpokenLanguage(e.target.value)}
                options={LANGUAGE_OPTIONS}
                error={errors.spokenLanguage}
              />
              <Select
                label={t("fields.gender")}
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                options={[
                  { label: t("fields.selectGender"), value: "" },
                  { label: t("fields.male"), value: "M" },
                  { label: t("fields.female"), value: "F" }
                ]}
                error={errors.gender}
              />

              <Select
                label={t("fields.country")}
                value={country}
                onChange={handleCountryChange}
                options={[
                  { label: t("fields.selectCountry"), value: "" },
                  ...Object.keys(COUNTRY_CONFIG).map((c) => ({
                    label: c,
                    value: c
                  }))
                ]}
                error={errors.country}
              />
              <Input label={t("fields.city")} value={city} setValue={setCity} error={errors.city} />

              <Input
                label={t("fields.state")}
                value={stateProvince}
                setValue={setStateProvince}
                optional
                optionalLabel={t("create.optional")}
              />

              <div className="form-field">
                <label htmlFor="whatsapp">{t("fields.whatsapp")}</label>
                <div className="phone-row">
                  <div className="code-chip">{countryCode || t("fields.phoneCode")}</div>
                  <input
                    id="whatsapp"
                    className="text-input"
                    value={whatsapp}
                    onChange={handleWhatsappChange}
                    placeholder={t("fields.whatsappPlaceholder")}
                    style={errors.whatsapp ? { borderColor: "#b0432f" } : undefined}
                  />
                </div>
                {errors.whatsapp && <span className="field-error">{errors.whatsapp}</span>}
              </div>
            </div>

            <div className="button-row">
              <button className="ghost-button" onClick={() => navigate(-1)}>
                {t("create.btnBack")}
              </button>
              <button className="button" onClick={handleSave}>
                {isEditMode ? t("create.btnUpdate") : t("create.btnCreate")}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const Input = ({ label, value, setValue, error, optional, optionalLabel }) => (
  <div className="form-field">
    <label>
      {label} {optional && <span className="field-note">({optionalLabel || "Optional"})</span>}
    </label>
    <input
      className="text-input"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      style={error ? { borderColor: "#b0432f" } : undefined}
    />
    {error && <span className="field-error">{error}</span>}
  </div>
);

const Select = ({ label, value, onChange, options, error }) => (
  <div className="form-field">
    <label>{label}</label>
    <select
      className="select-input"
      value={value}
      onChange={onChange}
      style={error ? { borderColor: "#b0432f" } : undefined}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
    {error && <span className="field-error">{error}</span>}
  </div>
);

export default CreateElderProfile;
